"use client";
import {
  Badge,
  Button,
  createTableColumn,
  DataGrid,
  DataGridBody,
  DataGridCell,
  DataGridHeader,
  DataGridHeaderCell,
  DataGridRow,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Field,
  Input,
  Link,
  makeStyles,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  Spinner,
  Tab,
  TableColumnDefinition,
  TabList,
  Textarea,
  tokens,
  Tooltip,
} from "@fluentui/react-components";
import {
  ArrowLeft20Regular,
  Delete20Regular,
  Link16Regular,
  Search20Regular,
} from "@fluentui/react-icons";
import {
  getAccessTokenUsingMsal,
  useApiGet,
  useMsal,
} from "@skanska/vsa-client-auth";
import { getDocumentIcon } from "@skanska/vsa-file-icon";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { ButtonWithLoading } from "../components/ButtonWithLoading";
import { DocumentHealthCheckPanel } from "../components/systemadmin/DocumentHealthCheckPanel";
import { apiConfig } from "../config/AppConfig";
import { loginRequest } from "../config/MsalConfig";
import { useAdminDeleteTool } from "../hooks/useTools";
import { ApiEndpoints } from "../models/ApiEndpoints";
import { ClientRoutes } from "../models/ClientRoutes";
import PagedResult from "../models/PagedResult";
import { Tool } from "../models/Tool";
import { sanitizeFileName } from "../services/ToolService";
import { SkanskaColorTokens } from "../style/colors";
import { useGetUserAccessLevel } from "../hooks/useGetUserAccessLevel";

const useClasses = makeStyles({
  root: {
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    height: "100%",
    padding: tokens.spacingVerticalM,
    gap: tokens.spacingVerticalL,
  },
  header: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalM,
  },
  searchContainer: {
    display: "flex",
    gap: tokens.spacingHorizontalM,
    alignItems: "end",
  },
  searchInput: {
    minWidth: "300px",
  },
  resultRow: {
    display: "flex",
    gap: tokens.spacingHorizontalM,
    paddingTop: tokens.spacingVerticalXS,
    paddingBottom: tokens.spacingVerticalXS,
  },
  resultLabel: {
    fontWeight: tokens.fontWeightSemibold,
    minWidth: "100px",
  },
  actions: {
    display: "flex",
    gap: tokens.spacingHorizontalM,
    marginTop: tokens.spacingVerticalM,
  },
  deleteButton: {
    backgroundColor: SkanskaColorTokens.color_tertiary_red_30,
    ":hover, :focus, :active": {
      backgroundColor: SkanskaColorTokens.color_tertiary_red,
      color: SkanskaColorTokens.color_secondary_white,
    },
  },
  cellWrap: {
    whiteSpace: "normal",
    wordBreak: "break-word",
    overflowWrap: "break-word",
  },
  clickableRow: {
    cursor: "pointer",
    ":hover": {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  selectedRow: {
    backgroundColor: tokens.colorNeutralBackground1Selected,
  },
  dialogContent: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalS,
  },
  healthSection: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalM,
  },
  healthSummary: {
    display: "flex",
    gap: tokens.spacingHorizontalM,
    alignItems: "center",
  },
  tabContent: {
    paddingTop: tokens.spacingVerticalM,
    overflow: "auto",
    flex: 1,
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalM,
  },
});

function DocumentManagementPage() {
  const classes = useClasses();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { instance, inProgress } = useMsal();
  const [activeTab, setActiveTab] = useState<"search" | "health">("search");
  const [searchQuery, setSearchQuery] = useState("");
  const [activeQuery, setActiveQuery] = useState("");
  const [selectedTool, setSelectedTool] = useState<Tool | null>(null);
  const [deleteComment, setDeleteComment] = useState("");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteSuccess, setDeleteSuccess] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [fileValidationStatus, setFileValidationStatus] = useState<
    boolean | undefined
  >(undefined);
  const [isValidating, setIsValidating] = useState(false);

  const { data: searchResults, isLoading: searchLoading } = useApiGet<
    PagedResult<Tool>
  >(
    `${ApiEndpoints.ToolAdminSearchDocuments}?query=${encodeURIComponent(activeQuery)}&page=1&pageSize=20`,
    null,
    `admin-doc-search-${activeQuery}`
  );

  const { mutateAsync: adminDelete, isPending: deleteIsPending } =
    useAdminDeleteTool(selectedTool?.toolId ?? "");

  // Validate file only when a tool is selected
  const validateSelectedFile = useCallback(async () => {
    if (!selectedTool?.fileUniqueId) {
      setFileValidationStatus(false);
      return;
    }

    setIsValidating(true);
    setFileValidationStatus(undefined);

    try {
      const accessToken = await getAccessTokenUsingMsal(
        inProgress,
        instance,
        loginRequest.scopes,
        apiConfig.tenantId
      );

      const response = await fetch(
        `${apiConfig.apiUrl}/${ApiEndpoints.ToolAdminValidateFile}/${encodeURIComponent(selectedTool.fileUniqueId)}`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );
      setFileValidationStatus(response.ok && (await response.json()));
    } catch {
      setFileValidationStatus(false);
    } finally {
      setIsValidating(false);
    }
  }, [selectedTool, inProgress, instance]);

  useEffect(() => {
    if (selectedTool) {
      /* eslint-disable-next-line react-hooks/set-state-in-effect */
      validateSelectedFile();
    }
  }, [selectedTool, validateSelectedFile]);

  const accessLevel = useGetUserAccessLevel();

  if (accessLevel > -1 && accessLevel < 5) {
    navigate("/");
  }

  const handleSearch = () => {
    if (searchQuery.trim()) {
      setActiveQuery(searchQuery.trim());
      setSelectedTool(null);
      setDeleteSuccess(null);
      setDeleteError(null);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  const handleDelete = async () => {
    if (!selectedTool?.toolId || !deleteComment.trim()) return;

    try {
      await adminDelete({ comment: deleteComment });
      setDeleteSuccess(selectedTool.title);
      setSelectedTool(null);
      setActiveQuery("");
      setSearchQuery("");
      setDeleteComment("");
      setDeleteDialogOpen(false);
    } catch {
      setDeleteError(t("documentManagement.deleteError"));
      setDeleteDialogOpen(false);
    }
  };

  const handleCloseInfoDialog = () => {
    setSelectedTool(null);
    setFileValidationStatus(undefined);
    setDeleteComment("");
  };

  const columns: TableColumnDefinition<Tool>[] = [
    createTableColumn<Tool>({
      columnId: "type",
      renderHeaderCell: () => t("type"),
      renderCell: (item) => (
        <Tooltip
          content={item.fileUniqueId ? t("document") : t("link")}
          relationship="label"
        >
          {item.fileType ? (
            <img
              src={getDocumentIcon(item.fileType)}
              alt="type"
              width={16}
              height={16}
            />
          ) : (
            <Link16Regular />
          )}
        </Tooltip>
      ),
    }),
    createTableColumn<Tool>({
      columnId: "title",
      renderHeaderCell: () => t("title"),
      renderCell: (item) => item.title,
    }),
    createTableColumn<Tool>({
      columnId: "name",
      renderHeaderCell: () => t("name"),
      renderCell: (item) => (
        <span className={classes.cellWrap}>
          {sanitizeFileName(item.webUrl)}
        </span>
      ),
    }),
    createTableColumn<Tool>({
      columnId: "toolId",
      renderHeaderCell: () => t("documentManagement.toolId"),
      renderCell: (item) => (
        <span className={classes.cellWrap}>{item.toolId ?? ""}</span>
      ),
    }),
    createTableColumn<Tool>({
      columnId: "url",
      renderHeaderCell: () => t("documentManagement.url"),
      renderCell: (item) => (
        <span className={classes.cellWrap}>
          <Link
            href={item.webUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
          >
            {item.webUrl}
          </Link>
        </span>
      ),
    }),
  ];

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <Button
          icon={<ArrowLeft20Regular />}
          appearance="subtle"
          onClick={() => navigate(ClientRoutes.SystemAdmin)}
        />
        <h2>{t("documentManagement.title")}</h2>
      </div>

      <TabList
        selectedValue={activeTab}
        onTabSelect={(_, data) =>
          setActiveTab(data.value as "search" | "health")
        }
      >
        <Tab value="search">{t("documentManagement.tabs.search")}</Tab>
        <Tab value="health">{t("documentManagement.tabs.health")}</Tab>
      </TabList>

      {activeTab === "search" && (
        <div className={classes.tabContent}>
          <div className={classes.searchContainer}>
            <Field label={t("documentManagement.searchLabel")}>
              <Input
                className={classes.searchInput}
                placeholder={t("documentManagement.searchPlaceholder")}
                value={searchQuery}
                onChange={(_, data) => setSearchQuery(data.value)}
                onKeyDown={handleKeyDown}
                contentAfter={<Search20Regular />}
              />
            </Field>
            <ButtonWithLoading
              onClick={handleSearch}
              isLoading={searchLoading && !!activeQuery}
              text={t("documentManagement.search")}
            />
          </div>

          {deleteSuccess && (
            <MessageBar intent="success">
              <MessageBarBody>
                <MessageBarTitle>
                  {t("documentManagement.deleteSuccess")}
                </MessageBarTitle>
                {t("documentManagement.deleteSuccessMessage", {
                  title: deleteSuccess,
                })}
              </MessageBarBody>
            </MessageBar>
          )}

          {deleteError && (
            <MessageBar intent="error">
              <MessageBarBody>
                <MessageBarTitle>
                  {t("documentManagement.error")}
                </MessageBarTitle>
                {deleteError}
              </MessageBarBody>
            </MessageBar>
          )}

          {searchLoading && activeQuery && <Spinner label={t("loading")} />}

          {!searchLoading &&
            activeQuery &&
            searchResults &&
            searchResults.items.length > 0 && (
              <DataGrid
                items={searchResults.items}
                columns={columns}
                getRowId={(item) => item.versionId}
                resizableColumns
                columnSizingOptions={{
                  type: { idealWidth: 40, minWidth: 40 },
                  title: { idealWidth: 200, minWidth: 120 },
                  name: { idealWidth: 200, minWidth: 120 },
                  toolId: { idealWidth: 280, minWidth: 150 },
                  url: { idealWidth: 300, minWidth: 150 },
                }}
              >
                <DataGridHeader>
                  <DataGridRow>
                    {({ renderHeaderCell }) => (
                      <DataGridHeaderCell>
                        {renderHeaderCell()}
                      </DataGridHeaderCell>
                    )}
                  </DataGridRow>
                </DataGridHeader>
                <DataGridBody<Tool>>
                  {({ item, rowId }) => (
                    <DataGridRow<Tool>
                      key={rowId}
                      className={classes.clickableRow}
                      onClick={() => setSelectedTool(item)}
                    >
                      {({ renderCell }) => (
                        <DataGridCell>{renderCell(item)}</DataGridCell>
                      )}
                    </DataGridRow>
                  )}
                </DataGridBody>
              </DataGrid>
            )}

          {!searchLoading &&
            activeQuery &&
            searchResults &&
            searchResults.items.length === 0 && (
              <MessageBar intent="warning">
                <MessageBarBody>
                  {t("documentManagement.noResults", { query: activeQuery })}
                </MessageBarBody>
              </MessageBar>
            )}
        </div>
      )}

      {activeTab === "health" && (
        <div className={classes.tabContent}>
          <DocumentHealthCheckPanel />
        </div>
      )}

      <Dialog
        open={!!selectedTool}
        onOpenChange={(_, data) => {
          if (!data.open) handleCloseInfoDialog();
        }}
      >
        <DialogSurface>
          <DialogBody>
            <DialogTitle>{t("documentManagement.documentInfo")}</DialogTitle>
            <DialogContent className={classes.dialogContent}>
              {selectedTool && (
                <>
                  <div className={classes.resultRow}>
                    <span className={classes.resultLabel}>{t("type")}:</span>
                    <span>
                      {selectedTool.fileType ? (
                        <img
                          src={getDocumentIcon(selectedTool.fileType)}
                          alt="type"
                          width={20}
                          height={20}
                        />
                      ) : (
                        <Link16Regular />
                      )}
                    </span>
                  </div>
                  <div className={classes.resultRow}>
                    <span className={classes.resultLabel}>{t("title")}:</span>
                    <span>{selectedTool.title}</span>
                  </div>
                  <div className={classes.resultRow}>
                    <span className={classes.resultLabel}>
                      {t("documentManagement.toolId")}:
                    </span>
                    <span>{selectedTool.toolId}</span>
                  </div>
                  <div className={classes.resultRow}>
                    <span className={classes.resultLabel}>
                      {t("documentManagement.url")}:
                    </span>
                    <Link
                      href={selectedTool.webUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {selectedTool.webUrl}
                    </Link>
                  </div>
                  {selectedTool.adminDisplayName && (
                    <div className={classes.resultRow}>
                      <span className={classes.resultLabel}>
                        {t("documentManagement.adminName")}:
                      </span>
                      <span>{selectedTool.adminDisplayName}</span>
                    </div>
                  )}
                  <div className={classes.resultRow}>
                    <span className={classes.resultLabel}>
                      {t("documentManagement.fileStatus")}:
                    </span>
                    {isValidating || fileValidationStatus === undefined ? (
                      <Spinner size="tiny" />
                    ) : (
                      <Badge
                        appearance="filled"
                        color={fileValidationStatus ? "success" : "danger"}
                      >
                        {fileValidationStatus
                          ? t("documentManagement.fileValid")
                          : t("documentManagement.fileInvalid")}
                      </Badge>
                    )}
                  </div>
                </>
              )}
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={handleCloseInfoDialog}>
                {t("cancel")}
              </Button>
              <Button
                className={classes.deleteButton}
                icon={<Delete20Regular />}
                appearance="primary"
                onClick={() => setDeleteDialogOpen(true)}
              >
                {t("documentManagement.deleteDocument")}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      <Dialog
        modalType="alert"
        open={deleteDialogOpen}
        onOpenChange={(_, data) => setDeleteDialogOpen(data.open)}
      >
        <DialogSurface>
          <DialogBody>
            <DialogTitle>
              {t("documentManagement.deleteDialogTitle")}
            </DialogTitle>
            <DialogContent>
              <p>
                {t("documentManagement.deleteDialogMessage", {
                  title: selectedTool?.title,
                })}
              </p>
              <Field
                label={t("documentManagement.deleteCommentLabel")}
                required
              >
                <Textarea
                  value={deleteComment}
                  onChange={(_, data) => setDeleteComment(data.value)}
                  placeholder={t("documentManagement.deleteCommentPlaceholder")}
                />
              </Field>
            </DialogContent>
            <DialogActions>
              <Button
                appearance="secondary"
                onClick={() => setDeleteDialogOpen(false)}
              >
                {t("cancel")}
              </Button>
              <ButtonWithLoading
                onClick={handleDelete}
                isLoading={deleteIsPending}
                text={t("documentManagement.confirmDelete")}
                disabled={!deleteComment.trim()}
              />
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
}

export default DocumentManagementPage;
